import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import './CLOs.css';

const CLO_TYPES = ['K1', 'K2', 'K3', 'K4', 'K5', 'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'V1', 'V2', 'V3', 'V4', 'V5'];

const REQUIRED_TOOLS = [
    { nameEn: 'HM1', nameAr: 'HM1', questions: [{ type: 'K1', max: 0 }] },
    { nameEn: 'HM2', nameAr: 'HM2', questions: [{ type: 'K1', max: 0 }] },
    { nameEn: 'Qz1', nameAr: 'Qz1', questions: [{ type: 'K1', max: 0 }] },
    { nameEn: 'Qz2', nameAr: 'Qz2', questions: [{ type: 'K1', max: 0 }] },
    { nameEn: 'Md1', nameAr: 'Md1', questions: [{ type: 'K1', max: 0 }] },
    { nameEn: 'Md2', nameAr: 'Md2', questions: [{ type: 'K1', max: 0 }] },
    { nameEn: 'Lab', nameAr: 'Lab', questions: [{ type: 'K1', max: 0 }] },
    { nameEn: 'Final', nameAr: 'Final', questions: [{ type: 'K1', max: 0 }] },
];

const CLOs = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isAr = i18n.language === 'ar';

    // Data States
    const [course, setCourse] = useState(null);
    const [gradesData, setGradesData] = useState({ students: [], assessmentTools: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [unsavedGrades, setUnsavedGrades] = useState(() => {
        try {
            const stored = localStorage.getItem(`pending_grades_${courseId}`);
            return stored ? JSON.parse(stored) : {};
        } catch (e) { return {}; }
    });
    const [unsavedMaxScores, setUnsavedMaxScores] = useState(() => {
        try {
            const stored = localStorage.getItem(`pending_max_scores_${courseId}`);
            return stored ? JSON.parse(stored) : {};
        } catch (e) { return {}; }
    });

    // Modal States
    const [showCLOMatrixModal, setShowCLOMatrixModal] = useState(false);
    const [showCLOFormModal, setShowCLOFormModal] = useState(false);
    const [showMarksModal, setShowMarksModal] = useState(false);
    const [showStudentGradesModal, setShowStudentGradesModal] = useState(false);
    const [showAddToolModal, setShowAddToolModal] = useState(false);

    // Initial Fetch
    const fetchData = useCallback(async (silent = true) => {
        try {
            if (!silent) setLoading(true);
            const [courseRes, gradesRes] = await Promise.all([
                api.get(`/teacher/courses`),
                api.get(`/teacher/courses/${courseId}/grades?t=${Date.now()}`)
            ]);

            if (courseRes.data.success) {
                const currentCourse = courseRes.data.data.find(c => c.course_id === parseInt(courseId));
                setCourse(currentCourse);
            }
            if (gradesRes.data.success) {
                const data = gradesRes.data.data;

                // Parse all numeric values from backend
                if (data.assessmentTools) {
                    data.assessmentTools = data.assessmentTools.map(tool => {
                        const parsedGradesByStudent = {};
                        if (tool.gradesByStudent) {
                            Object.entries(tool.gradesByStudent).forEach(([sId, qGrades]) => {
                                parsedGradesByStudent[sId] = {};
                                Object.entries(qGrades).forEach(([qId, score]) => {
                                    parsedGradesByStudent[sId][qId] = (score === null || score === undefined) ? null : parseFloat(score);
                                });
                            });
                        }

                        return {
                            ...tool,
                            questions: tool.questions.map(q => ({
                                ...q,
                                question_type: (q.question_type || '').toUpperCase(),
                                max_score: parseFloat(q.max_score)
                            })),
                            gradesByStudent: parsedGradesByStudent
                        };
                    });
                }

                // Apply pending grades from localStorage if same course
                const stored = localStorage.getItem(`pending_grades_${courseId}`);
                if (stored) {
                    const pending = JSON.parse(stored);
                    setUnsavedGrades(pending);

                    // Merge pending into gradesData for UI consistency
                    if (data.assessmentTools) {
                        data.assessmentTools = data.assessmentTools.map(tool => {
                            const newGradesByStudent = { ...tool.gradesByStudent };
                            Object.entries(pending).forEach(([key, score]) => {
                                const [sId, qId] = key.split('_');
                                if (tool.questions.some(q => q.question_id === parseInt(qId))) {
                                    if (!newGradesByStudent[sId]) newGradesByStudent[sId] = {};
                                    newGradesByStudent[sId][qId] = score;
                                }
                            });
                            return { ...tool, gradesByStudent: newGradesByStudent };
                        });
                    }
                }

                const storedMaxScores = localStorage.getItem(`pending_max_scores_${courseId}`);
                if (storedMaxScores) {
                    const pending = JSON.parse(storedMaxScores);
                    setUnsavedMaxScores(pending);
                    if (data.assessmentTools) {
                        data.assessmentTools = data.assessmentTools.map(tool => ({
                            ...tool,
                            questions: tool.questions.map(q => {
                                if (pending[q.question_id] !== undefined) {
                                    return { ...q, max_score: pending[q.question_id] };
                                }
                                return q;
                            })
                        }));
                    }
                }

                setGradesData(data);
            }
        } catch (error) {
            console.error('Error fetching CLO data:', error);
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    useEffect(() => {
        fetchData(false); // Initial load is NOT silent
    }, [fetchData]);

    // Save unsavedGrades to localStorage
    useEffect(() => {
        if (Object.keys(unsavedGrades).length > 0) {
            localStorage.setItem(`pending_grades_${courseId}`, JSON.stringify(unsavedGrades));
        } else {
            localStorage.removeItem(`pending_grades_${courseId}`);
        }
    }, [unsavedGrades, courseId]);

    useEffect(() => {
        if (Object.keys(unsavedMaxScores).length > 0) {
            localStorage.setItem(`pending_max_scores_${courseId}`, JSON.stringify(unsavedMaxScores));
        } else {
            localStorage.removeItem(`pending_max_scores_${courseId}`);
        }
    }, [unsavedMaxScores, courseId]);

    // *** Dynamic Tools from database ***
    const assessmentTools = useMemo(() => {
        return gradesData.assessmentTools.map(tool => ({
            ...tool,
            colorClass: getToolColorClass(tool.tool_name_en || tool.tool_name_ar)
        }));
    }, [gradesData.assessmentTools]);

    // Helper to get color class based on tool name
    function getToolColorClass(name) {
        const n = (name || '').toLowerCase();
        if (n.includes('hm') || n.includes('واجب')) return 'hm';
        if (n.includes('qz') || n.includes('قصير')) return 'qz';
        if (n.includes('md1') || n.includes('نصفي 1')) return 'md1';
        if (n.includes('md2') || n.includes('نصفي 2')) return 'md2';
        if (n.includes('lab') || n.includes('مختبر')) return 'lab';
        if (n.includes('final') || n.includes('نهائي')) return 'fnl';
        return 'hm';
    }

    // Handlers
    const formatGrade = (val) => {
        if (val === null || val === undefined || val === '') return '';
        return Number(val).toFixed(2);
    };

    const handleGradeInput = (studentId, questionId, score, maxScore) => {
        if (!questionId) return;

        let val = (score === '' || score === null || score === undefined) ? null : parseFloat(score);
        if (val !== null) {
            if (val > maxScore) val = maxScore;
            if (val < 0) val = 0;
            val = parseFloat(val.toFixed(2));
        }

        // Update local state ONLY (no API call)
        setUnsavedGrades(prev => ({
            ...prev,
            [`${studentId}_${questionId}`]: val
        }));

        setGradesData(prev => ({
            ...prev,
            assessmentTools: prev.assessmentTools.map(tool => ({
                ...tool,
                gradesByStudent: {
                    ...tool.gradesByStudent,
                    [studentId]: {
                        ...tool.gradesByStudent[studentId],
                        [questionId]: val
                    }
                }
            }))
        }));
    };

    const handleMaxScoreInput = (questionId, maxScore) => {
        if (!questionId) return;
        const val = parseFloat(parseFloat(maxScore).toFixed(2));

        setUnsavedMaxScores(prev => ({
            ...prev,
            [questionId]: val
        }));

        setGradesData(prev => ({
            ...prev,
            assessmentTools: prev.assessmentTools.map(tool => ({
                ...tool,
                questions: tool.questions.map(q => {
                    if (q.question_id === questionId) {
                        return { ...q, max_score: val };
                    }
                    return q;
                })
            }))
        }));
    };

    const handleBulkSave = async () => {
        const hasUnsavedGrades = Object.keys(unsavedGrades).length > 0;
        const hasUnsavedMaxScores = Object.keys(unsavedMaxScores).length > 0;

        if (!hasUnsavedGrades && !hasUnsavedMaxScores) return;

        try {
            setSaving(true);
            const promises = [];

            if (hasUnsavedGrades) {
                const gradesArray = Object.entries(unsavedGrades).map(([key, score]) => {
                    const [studentId, questionId] = key.split('_');
                    return { studentId: parseInt(studentId), questionId: parseInt(questionId), score };
                });
                promises.push(api.post('/teacher/grades/bulk', { grades: gradesArray }));
            }

            if (hasUnsavedMaxScores) {
                const questionsArray = Object.entries(unsavedMaxScores).map(([qId, mScore]) => ({
                    questionId: parseInt(qId),
                    maxScore: mScore
                }));
                promises.push(api.post('/teacher/questions/bulk', { questions: questionsArray }));
            }

            const results = await Promise.all(promises);
            const allSuccess = results.every(res => res.data.success);

            if (allSuccess) {
                setUnsavedGrades({});
                setUnsavedMaxScores({});
                localStorage.removeItem(`pending_grades_${courseId}`);
                localStorage.removeItem(`pending_max_scores_${courseId}`);
                fetchData(true);
            }
        } catch (error) {
            console.error('Error in bulk save:', error);
            alert(isAr ? 'حدث خطأ أثناء حفظ التعديلات' : 'Error saving changes');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTool = async (toolId, toolName) => {
        if (!toolId) return;
        if (assessmentTools.length <= 1) return;
        if (!window.confirm(isAr ? `هل أنت متأكد من حذف ${toolName}؟` : `Delete ${toolName}?`)) return;

        try {
            setSaving(true);
            await api.delete(`/teacher/assessment-tools/${toolId}`);
            fetchData();
        } catch (error) {
            console.error('Error deleting tool:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleAddQuestion = async (toolId) => {
        if (!toolId) return;
        try {
            setSaving(true);
            await api.post('/teacher/questions', {
                toolId,
                questionType: 'K1',
                maxScore: 0
            });
            fetchData();
        } catch (error) {
            console.error('Error adding question:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleQuestionUpdate = async (questionId, updates) => {
        if (!questionId) return;

        // Map updates to snake_case for local state
        const stateUpdates = {};
        if (updates.maxScore !== undefined) stateUpdates.max_score = parseFloat(updates.maxScore);
        if (updates.questionType !== undefined) {
            stateUpdates.question_type = updates.questionType.toUpperCase();
            updates.questionType = updates.questionType.toUpperCase(); // Also update payload
        }

        // Optimistic UI update
        setGradesData(prev => {
            const newTools = (prev.assessmentTools || []).map(tool => {
                const qIdx = tool.questions.findIndex(q => q.question_id === questionId);
                if (qIdx === -1) return tool;

                const newQuestions = tool.questions.map(q =>
                    q.question_id === questionId ? { ...q, ...stateUpdates } : q
                );

                let newMax = tool.max_score;
                if (updates.maxScore !== undefined) {
                    const oldMax = parseFloat(tool.questions[qIdx].max_score) || 0;
                    const diff = parseFloat(updates.maxScore) - oldMax;
                    newMax = (parseFloat(tool.max_score) || 0) + diff;
                }

                return { ...tool, questions: newQuestions, max_score: newMax };
            });
            return { ...prev, assessmentTools: newTools };
        });

        try {
            setSaving(true);
            await api.put(`/teacher/questions/${questionId}`, updates);
            // No need to fetchData immediately if optimism is reliable, 
            // but silent background fetch doesn't hurt.
            fetchData(true);
        } catch (error) {
            console.error('Error updating question:', error);
            fetchData(true); // Restore from server on error
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        if (!questionId) return;
        if (!window.confirm(isAr ? 'هل أنت متأكد من حذف هذا السؤال؟' : 'Are you sure you want to delete this question?')) return;

        try {
            setSaving(true);
            await api.delete(`/teacher/questions/${questionId}`);
            fetchData();
        } catch (error) {
            console.error('Error deleting question:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleAddTool = async (toolData) => {
        try {
            setSaving(true);
            await api.post('/teacher/assessment-tools', {
                courseId,
                toolNameAr: toolData.nameAr,
                toolNameEn: toolData.nameEn,
                questions: toolData.questions.map(q => ({
                    questionType: q.type,
                    maxScore: q.max
                }))
            });
            setShowAddToolModal(false);
            fetchData();
        } catch (error) {
            console.error('Error adding tool:', error);
        } finally {
            setSaving(false);
        }
    };

    const missingTools = useMemo(() => {
        return REQUIRED_TOOLS.filter(req =>
            !assessmentTools.some(at =>
                (at.tool_name_en === req.nameEn) || (at.tool_name_ar === req.nameAr)
            )
        );
    }, [assessmentTools]);

    // Calculations
    // Helper to identify category for any tool - uses a catch-all logic to avoid missing marks
    const getCategoryFromTool = useCallback((tool) => {
        const name = (tool.tool_name_en || tool.tool_name_ar || '').toLowerCase();

        // Final
        if (name.includes('final') || name.includes('نهائي') || name.includes('فاينل')) return 'final';

        // Midterm 2
        if (name.includes('md2') || name.includes('نصفي 2') || name.includes('ميد 2') || name.includes('ثان') || (name.includes('midterm') && (name.includes('2') || name.includes('two')))) return 'md2';

        // Midterm 1 (default for 'midterm' without number)
        if (name.includes('md1') || name.includes('نصفي 1') || name.includes('ميد 1') || name.includes('اول') || name.includes('أول') || name.includes('midterm') || name.includes('نصفي')) return 'md1';

        // Lab
        if (name.includes('lab') || name.includes('مختبر') || name.includes('عملي') || name.includes('معمل') || name.includes('project') || name.includes('مشروع')) return 'lab';

        // Quizzes
        if (name.includes('qz') || name.includes('قصير') || name.includes('كويز') || name.includes('quiz')) return 'qz';

        // Catch-all (Homework, Assignments, etc.)
        return 'hm';
    }, []);

    // Helper to get score by category
    const getScoreByCategory = useCallback((studentId, category) => {
        let total = 0;
        assessmentTools.forEach(tool => {
            if (getCategoryFromTool(tool) === category) {
                tool.questions.forEach(q => {
                    const score = tool.gradesByStudent?.[studentId]?.[q.question_id];
                    total += parseFloat(score || 0);
                });
            }
        });
        return +(total.toFixed(2)); // Use parenthesis for precision
    }, [assessmentTools, getCategoryFromTool]);

    // Helper to get total max for a category
    const getMaxByCategory = useCallback((category) => {
        let total = 0;
        assessmentTools.forEach(tool => {
            if (getCategoryFromTool(tool) === category) {
                tool.questions.forEach(q => {
                    total += parseFloat(q.max_score || 0);
                });
            }
        });
        return +(total.toFixed(2));
    }, [assessmentTools, getCategoryFromTool]);

    const getStudentTotal = useCallback((studentId) => {
        let total = 0;
        assessmentTools.forEach(tool => {
            tool.questions.forEach(q => {
                const score = tool.gradesByStudent?.[studentId]?.[q.question_id];
                total += parseFloat(score || 0);
            });
        });
        return +total.toFixed(2); // Consistent rounding
    }, [assessmentTools]);

    const getQuestionAverage = (questionId) => {
        const students = gradesData.students;
        if (!students.length) return 0;

        let sum = 0;
        let count = 0;

        // Find the tool that contains this question
        const tool = assessmentTools.find(t => t.questions.some(q => q.question_id === questionId));
        if (!tool) return 0;

        students.forEach(student => {
            const score = tool.gradesByStudent?.[student.student_id]?.[questionId];
            if (score !== undefined && score !== null && score !== '') {
                sum += parseFloat(score);
                count++;
            }
        });

        return count > 0 ? sum / students.length : 0; // Average over all students
    };

    const calculateGrade = (total) => {
        const t = Math.round(total);
        if (t >= 95) return 'A+';
        if (t >= 90) return 'A';
        if (t >= 85) return 'B+';
        if (t >= 80) return 'B';
        if (t >= 75) return 'C+';
        if (t >= 70) return 'C';
        if (t >= 65) return 'D+';
        if (t >= 60) return 'D';
        return 'F';
    };

    // Excel Export Helpers
    const applyHeaderStyle = (cell, isMain = true) => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: isMain ? 'FF1E293B' : 'FFF1F5F9' }
        };
        cell.font = {
            color: { argb: isMain ? 'FFFFFFFF' : 'FF1E293B' },
            bold: true,
            size: 11
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    };

    const applyCellStyle = (cell, isBold = false, bgColor = null) => {
        if (bgColor) {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: bgColor.replace('#', 'FF') }
            };
        }
        cell.font = { bold: isBold, size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    };

    // Export CLO Matrix Excel
    // Export CLO Matrix Excel
    const fillCLOMatrixSheet = (sheet) => {
        const { students, assessmentTools } = gradesData;
        const allQuestions = [];
        assessmentTools.forEach(tool => {
            tool.questions.forEach(q => {
                allQuestions.push({ ...q, toolName: tool.tool_name_en || tool.tool_name_ar });
            });
        });

        const totalPoints = allQuestions.reduce((sum, q) => sum + parseFloat(q.max_score || 0), 0);

        // Headers
        const toolRow = sheet.addRow([
            isAr ? 'م' : '#',
            isAr ? 'الرقم الجامعي' : 'ID',
            isAr ? 'الاسم' : 'Name',
            ...assessmentTools.flatMap(tool =>
                tool.questions.map(() => tool.tool_name_en || tool.tool_name_ar)
            ),
            isAr ? 'المجموع' : 'Total'
        ]);
        toolRow.eachCell(cell => applyHeaderStyle(cell));

        const maxRow = sheet.addRow([
            '', '', isAr ? 'الدرجة القصوى' : 'Max Score',
            ...allQuestions.map(q => parseFloat(q.max_score || 0)),
            +totalPoints.toFixed(2)
        ]);
        maxRow.eachCell(cell => applyHeaderStyle(cell, false));

        const avgRow = sheet.addRow([
            '', '', isAr ? 'المتوسط' : 'Average',
            ...allQuestions.map(q => +getQuestionAverage(q.question_id).toFixed(2)),
            +(gradesData.students.length > 0 ? gradesData.students.reduce((sum, s) => sum + getStudentTotal(s.student_id), 0) / gradesData.students.length : 0).toFixed(2)
        ]);
        avgRow.eachCell(cell => applyHeaderStyle(cell, false));

        const cloRow = sheet.addRow([
            '', '', isAr ? 'مخرجات التعلم' : 'PLOs',
            ...allQuestions.map(q => q.question_type),
            ''
        ]);
        cloRow.eachCell(cell => applyHeaderStyle(cell, false));

        // Student data
        students.forEach((student, idx) => {
            const rowData = [idx + 1, student.student_code, student.full_name_ar || student.full_name_en];
            let studentTotal = 0;
            allQuestions.forEach(q => {
                const tool = assessmentTools.find(at => at.questions.some(qu => qu.question_id === q.question_id));
                const score = parseFloat(tool?.gradesByStudent[student.student_id]?.[q.question_id] || 0);
                studentTotal += score;
                rowData.push(score);
            });
            rowData.push(+studentTotal.toFixed(2));
            const row = sheet.addRow(rowData);
            row.eachCell((cell, colNum) => applyCellStyle(cell, colNum === row.cellCount));
        });

        // Column widths
        sheet.getColumn(1).width = 5;
        sheet.getColumn(2).width = 15;
        sheet.getColumn(3).width = 30;
    };

    const handleExportCLOMatrix = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('PLO Matrix');
        fillCLOMatrixSheet(sheet);
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `PLO_Matrix_${course?.course_code || 'export'}.xlsx`);
    };

    // Export Marks Distribution Excel
    // Export Marks Distribution Excel (DYNAMIC VERSION)
    const fillMarksDistributionSheet = (sheet) => {
        const { students } = gradesData;

        // Same categories used in UI for consistency
        const cats = [
            { nameEn: 'Midterm I Exam', nameAr: 'الامتحان النصفي الأول', cat: 'md1' },
            { nameEn: 'Midterm II Exam', nameAr: 'الامتحان النصفي الثاني', cat: 'md2' },
            { nameEn: 'Lab Work', nameAr: 'العملي', cat: 'lab' },
            { nameEn: 'Class Work (Quizzes, Assignments, etc.)', nameAr: 'أعمال الفصل (واجبات، كويزات، إلخ)', cat: 'class' },
            { nameEn: 'Final Exam', nameAr: 'الامتحان النهائي', cat: 'final' }
        ];

        // Headers
        const headerStrings = [
            isAr ? 'م' : 'No.',
            isAr ? 'الرقم الجامعي' : 'Student ID',
            isAr ? 'الاسم' : 'Student Name',
            ...cats.map(c => isAr ? c.nameAr : c.nameEn),
            isAr ? 'المجموع' : 'Total'
        ];
        const headerRow = sheet.addRow(headerStrings);
        headerRow.eachCell(cell => applyHeaderStyle(cell));

        // Max Score Row
        const maxScores = [
            '', '', isAr ? 'الدرجة القصوى' : 'Max Score',
            ...cats.map(c => {
                const m = (c.cat === 'class') ? +(getMaxByCategory('hm') + getMaxByCategory('qz')).toFixed(2) : getMaxByCategory(c.cat);
                return m;
            }),
            +(getMaxByCategory('md1') + getMaxByCategory('md2') + getMaxByCategory('lab') + getMaxByCategory('hm') + getMaxByCategory('qz') + getMaxByCategory('final')).toFixed(2)
        ];
        const maxRow = sheet.addRow(maxScores);
        maxRow.eachCell(cell => applyHeaderStyle(cell, false));

        // Rows for each student
        students.forEach((student, idx) => {
            const rowArr = [idx + 1, student.student_code, student.full_name_en || student.full_name_ar];

            // Scores per category
            rowArr.push(getScoreByCategory(student.student_id, 'md1'));
            rowArr.push(getScoreByCategory(student.student_id, 'md2'));
            rowArr.push(getScoreByCategory(student.student_id, 'lab'));
            rowArr.push(+(getScoreByCategory(student.student_id, 'hm') + getScoreByCategory(student.student_id, 'qz')).toFixed(2));
            rowArr.push(getScoreByCategory(student.student_id, 'final'));

            // Total
            rowArr.push(+getStudentTotal(student.student_id).toFixed(2));

            const r = sheet.addRow(rowArr);
            r.eachCell((cell, colNum) => applyCellStyle(cell, colNum === r.cellCount));
        });

        // Column widths
        sheet.getColumn(1).width = 6;
        sheet.getColumn(2).width = 18;
        sheet.getColumn(3).width = 40;
        cats.forEach((_, i) => { sheet.getColumn(4 + i).width = 25; });
        sheet.getColumn(4 + cats.length).width = 15;
    };

    const handleExportMarksDistribution = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Marks Distribution');
        fillMarksDistributionSheet(sheet);
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Marks_Distribution_${course?.course_code || 'export'}.xlsx`);
    };

    // Export Student Grades Excel
    // Export Student Grades Excel
    // Export CLO Measurement Sheet
    const fillCLOMeasurementSheet = (sheet) => {
        const { students, assessmentTools } = gradesData;
        const domains = { 'Knowledge': ['K1', 'K2', 'K3', 'K4', 'K5'], 'Skills': ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8'], 'Values': ['V1', 'V2', 'V3', 'V4', 'V5'] };

        // Header Rows
        // Assessment tools in columns
        sheet.addRow([
            isAr ? 'مخرجات التعلم' : 'Learning Outcomes',
            '',
            ...assessmentTools.map(t => t.tool_name_en || t.tool_name_ar),
            isAr ? 'المجموع' : 'Total',
            isAr ? 'الفعلي' : 'Actual',
            isAr ? 'المستهدف' : 'Target'
        ]).eachCell(c => applyHeaderStyle(c));

        sheet.addRow([
            isAr ? 'المجال' : 'Domain',
            isAr ? 'الرمز' : 'Code',
            ...assessmentTools.map(t => ''),
            '', '', ''
        ]).eachCell(c => applyHeaderStyle(c, false));

        // Helper to calculate cell data
        const calcCell = (type, tool) => {
            const relevantQuestions = tool.questions.filter(q => q.question_type === type);
            if (relevantQuestions.length === 0) return null;

            let toolMax = 0;
            relevantQuestions.forEach(q => toolMax += parseFloat(q.max_score || 0));

            let totalActual = 0;
            students.forEach(student => {
                relevantQuestions.forEach(q => {
                    const score = tool.gradesByStudent?.[student.student_id]?.[q.question_id] || 0;
                    totalActual += parseFloat(score);
                });
            });

            const avg = students.length > 0 ? totalActual / students.length : 0;
            return { avg: avg.toFixed(2), max: toolMax.toFixed(2) };
        };

        const calcRow = (type) => {
            let tMax = 0, tAvg = 0, tData = {};
            assessmentTools.forEach(t => {
                const d = calcCell(type, t);
                if (d) {
                    tData[t.tool_id] = d;
                    tMax += parseFloat(d.max);
                    tAvg += parseFloat(d.avg);
                }
            });
            return { tData, tMax, tAvg: tAvg.toFixed(2), perc: tMax > 0 ? Math.round((tAvg / tMax) * 100) : 0 };
        };

        Object.entries(domains).forEach(([domain, types]) => {
            types.forEach((type, i) => {
                const { tData, tMax, tAvg, perc } = calcRow(type);
                const rowData = [
                    i === 0 ? domain : '',
                    type,
                    ...assessmentTools.map(t => tData[t.tool_id] ? `${tData[t.tool_id].avg}/${tData[t.tool_id].max}` : ''),
                    tMax > 0 ? `${tAvg}/${tMax.toFixed(2)}` : '',
                    tMax > 0 ? `${perc}%` : '',
                    tMax > 0 ? '70%' : ''
                ];
                sheet.addRow(rowData).eachCell((c, colN) => applyCellStyle(c, false));
            });
        });

        // Column widths
        sheet.getColumn(1).width = 15;
        sheet.getColumn(2).width = 10;
        assessmentTools.forEach((_, i) => sheet.getColumn(3 + i).width = 15);
    };

    // Export Student Grades Excel
    const fillStudentGradesSheet = (sheet) => {
        const { students } = gradesData;

        const headers = [
            isAr ? 'الرقم الجامعي' : 'Student ID',
            isAr ? 'اسم الطالب' : 'Student Name',
            'Hm', 'Qz', 'Md1', 'Md2', 'Lab',
            isAr ? 'أعمال الفصل' : 'Semester Works',
            isAr ? 'النهائي' : 'Final',
            isAr ? 'المجموع' : 'Total',
            isAr ? 'التقدير' : 'Grade'
        ];
        const headerRow = sheet.addRow(headers);
        headerRow.eachCell(cell => applyHeaderStyle(cell));

        const maxHm = getMaxByCategory('hm');
        const maxQz = getMaxByCategory('qz');
        const maxMd1 = getMaxByCategory('md1');
        const maxMd2 = getMaxByCategory('md2');
        const maxLab = getMaxByCategory('lab');
        const maxSemester = +(maxHm + maxQz + maxMd1 + maxMd2 + maxLab).toFixed(2);
        const maxFinal = getMaxByCategory('final');
        const maxTotal = +(maxSemester + maxFinal).toFixed(2);

        const maxRow = sheet.addRow(['', isAr ? 'الدرجة القصوى' : 'Max Score', maxHm, maxQz, maxMd1, maxMd2, maxLab, maxSemester, maxFinal, maxTotal, '']);
        maxRow.eachCell(cell => applyHeaderStyle(cell, false));

        students.forEach((student, idx) => {
            const hm = getScoreByCategory(student.student_id, 'hm');
            const qz = getScoreByCategory(student.student_id, 'qz');
            const md1 = getScoreByCategory(student.student_id, 'md1');
            const md2 = getScoreByCategory(student.student_id, 'md2');
            const lab = getScoreByCategory(student.student_id, 'lab');
            const final = getScoreByCategory(student.student_id, 'final');

            const semesterWorks = +(hm + qz + md1 + md2 + lab).toFixed(2);
            const total = +(semesterWorks + final).toFixed(2);
            const grade = calculateGrade(total);
            const isF = grade === 'F';

            const rowData = [
                student.student_code,
                isAr ? student.full_name_ar : (student.full_name_en || student.full_name_ar),
                hm, qz, md1, md2, lab,
                semesterWorks,
                final,
                total,
                grade
            ];

            const row = sheet.addRow(rowData);
            row.eachCell((cell, colNum) => {
                const isSemesterWorks = colNum === 8;
                const isTotal = colNum === 10;
                const isGrade = colNum === 11;
                applyCellStyle(cell, isTotal || isGrade || isSemesterWorks, isF ? '#FFE4E1' : (isSemesterWorks ? '#F8FAFC' : null));
            });
        });

        // Add Course Report to Excel (Combined)
        sheet.addRow([]);
        sheet.addRow([]);

        const counts = {
            'A+': 0, 'A': 0, 'B+': 0, 'B': 0, 'C+': 0, 'C': 0, 'D+': 0, 'D': 0, 'F': 0,
            'Pass': 0, 'Fail': 0, 'Total': students.length
        };

        students.forEach(student => {
            const studentTotal = getStudentTotal(student.student_id);
            const grade = calculateGrade(studentTotal);
            if (counts[grade] !== undefined) counts[grade]++;
            if (grade === 'F') counts['Fail']++; else counts['Pass']++;
        });

        const successPercent = students.length > 0 ? ((counts['Pass'] / students.length) * 100).toFixed(2) + '%' : '0.0%';

        // Success Rate
        const sRow = sheet.addRow(['Success Rate', successPercent]);
        sRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } };
        sRow.getCell(1).font = { bold: true };
        sRow.getCell(2).font = { bold: true };
        sheet.addRow([]);

        // Main Stats Table matching UI
        const grades = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D+', 'D', 'F', 'Denied Entry', 'In Progress', 'Incomplete', 'Pass', 'Fail', 'Withdrawn', 'Total'];
        const rhRow = sheet.addRow(grades);
        rhRow.eachCell(c => applyHeaderStyle(c));

        const gradeCounts = grades.map(g => {
            if (g === 'Total') return counts['Total'];
            if (['Denied Entry', 'In Progress', 'Incomplete', 'Withdrawn'].includes(g)) return 0;
            return counts[g] || 0;
        });
        const cRow = sheet.addRow(gradeCounts);
        cRow.eachCell(c => applyCellStyle(c));

        const getP = (v) => students.length > 0 ? ((v / students.length) * 100).toFixed(2) + '%' : '0%';
        const percents = grades.map((g, i) => g === 'Total' ? '100%' : getP(gradeCounts[i]));
        const pRow = sheet.addRow(percents);
        pRow.eachCell(c => applyCellStyle(c, false, '#F3E8FF'));

        sheet.addRow([]);

        // Simplified Summary Table (A, B, C, D, F, Total)
        const simplifiedHeaders = ['A', 'B', 'C', 'D', 'F', 'Total'];
        const simplifiedCounts = {
            'A': (counts['A+'] || 0) + (counts['A'] || 0),
            'B': (counts['B+'] || 0) + (counts['B'] || 0),
            'C': (counts['C+'] || 0) + (counts['C'] || 0),
            'D': (counts['D+'] || 0) + (counts['D'] || 0),
            'F': counts['F'] || 0,
            'Total': counts['Total']
        };

        const shRow = sheet.addRow(simplifiedHeaders);
        shRow.eachCell(c => applyHeaderStyle(c));

        const scRow = sheet.addRow(simplifiedHeaders.map(h => simplifiedCounts[h]));
        scRow.eachCell(c => applyCellStyle(c));
        scRow.getCell(6).font = { bold: true };

        sheet.getColumn(1).width = 15;
        sheet.getColumn(2).width = 35;
        for (let i = 3; i <= 11; i++) {
            sheet.getColumn(i).width = 12;
        }
    };

    const handleExportStudentGrades = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Student Grades');
        fillStudentGradesSheet(sheet);
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Student_Grades_${course?.course_code || 'export'}.xlsx`);
    };

    const handleExportAll = async () => {
        const workbook = new ExcelJS.Workbook();

        fillCLOMatrixSheet(workbook.addWorksheet('PLO Matrix'));
        fillCLOMeasurementSheet(workbook.addWorksheet('PLO Measurement'));
        fillStudentGradesSheet(workbook.addWorksheet('Student Grades'));
        fillMarksDistributionSheet(workbook.addWorksheet('Marks Distribution'));

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `All_Reports_${course?.course_code || 'export'}.xlsx`);
    };

    // *** RENDER MODALS ***
    const renderCourseHeader = () => {
        const user = JSON.parse(localStorage.getItem('teacher') || localStorage.getItem('user') || '{}');
        const lecturerName = isAr ? (course?.teacher_name_ar || user.full_name_ar || user.name) : (course?.teacher_name_en || user.full_name_en || user.name);
        const departmentName = isAr ? (course?.department_name_ar || course?.department) : (course?.department_name_en || course?.department);

        return (
            <div className="course-print-header" style={{ marginBottom: '20px', lineHeight: '1.6', fontSize: '1.0rem', color: 'black' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '10px' }}>
                    <span><strong>{isAr ? 'اسم المقرر:' : 'Course Title:'}</strong> {isAr ? course?.course_name_ar : course?.course_name_en}</span>
                    <span><strong>{isAr ? 'رمز المقرر:' : 'Course Code:'}</strong> {course?.course_code}</span>
                    <span><strong>{isAr ? 'التاريخ:' : 'Date:'}</strong> {new Date().toLocaleDateString('en-GB')}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    <span><strong>{isAr ? 'المحاضر:' : 'Lecturer:'}</strong> {lecturerName || 'Dr. Name'}</span>
                    <span><strong>{isAr ? 'القسم:' : 'Department:'}</strong> {departmentName || 'Computer Science'}</span>
                </div>
            </div>
        );
    };

    const renderCLOMatrixModal = () => (
        <div className="modal-overlay" onClick={() => setShowCLOMatrixModal(false)}>
            <div className="modal-content clo-modal-wide" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isAr ? 'مصفوفة PLO' : 'PLO Matrix'}</h2>
                    <button className="btn btn-outline" onClick={() => setShowCLOMatrixModal(false)}>
                        {isAr ? 'إغلاق' : 'Close'}
                    </button>
                </div>
                <div className="modal-body print-area" style={{ background: 'white', padding: '20px', color: 'black' }}>
                    {renderCourseHeader()}
                    <table className="report-table">
                        <thead>
                            {/* Row 1: Tools */}
                            <tr>
                                <th rowSpan="4">#</th>
                                <th rowSpan="4">ID</th>
                                <th className="header-label-cell">
                                    Assessment Tool
                                </th>
                                {gradesData.assessmentTools.map(tool => (
                                    <th key={tool.tool_id} colSpan={tool.questions.length || 1} className={tool.colorClass}>
                                        {tool.tool_name_en || tool.tool_name_ar}
                                    </th>
                                ))}
                                <th className="total-header total-label">Total</th>
                            </tr>
                            {/* Row 2: Max Scores */}
                            <tr className="header-row-max">
                                <th className="header-label-cell">
                                    Max Score
                                </th>
                                {gradesData.assessmentTools.flatMap(tool =>
                                    tool.questions.map((q, idx) => (
                                        <th key={`max-${tool.tool_id}-${idx}`}>
                                            {formatGrade(q.max_score)}
                                        </th>
                                    ))
                                )}
                                <th className="total-max-header">
                                    {formatGrade(gradesData.assessmentTools.reduce((acc, tool) => acc + tool.questions.reduce((s, q) => s + parseFloat(q.max_score || 0), 0), 0))}
                                </th>
                            </tr>
                            {/* Row 3: Averages */}
                            <tr className="average-row">
                                <th className="header-label-cell">
                                    Average
                                </th>
                                {gradesData.assessmentTools.flatMap(tool =>
                                    tool.questions.map((q, idx) => (
                                        <th key={`avg-${q.question_id}`}>
                                            {formatGrade(getQuestionAverage(q.question_id))}
                                        </th>
                                    ))
                                )}
                                <th className="total-avg-cell">
                                    {formatGrade(gradesData.students.length > 0
                                        ? gradesData.students.reduce((sum, s) => sum + getStudentTotal(s.student_id), 0) / gradesData.students.length
                                        : 0
                                    )}
                                </th>
                            </tr>
                            {/* Row 4: PLOs */}
                            <tr>
                                <th className="header-label-cell">
                                    PLOs
                                </th>
                                {gradesData.assessmentTools.flatMap(tool =>
                                    tool.questions.map((q, idx) => (
                                        <th key={`clo-${tool.tool_id}-${idx}`}>
                                            {q.question_type}
                                        </th>
                                    ))
                                )}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {gradesData.students.map((student, idx) => (
                                <tr key={student.student_id}>
                                    <td>{idx + 1}</td>
                                    <td>{student.student_code}</td>
                                    <td>{student.full_name_ar || student.full_name_en}</td>
                                    {gradesData.assessmentTools.flatMap(tool =>
                                        tool.questions.map((q, qIdx) => (
                                            <td key={`${tool.tool_id}-${qIdx}`} className={(tool.gradesByStudent?.[student.student_id]?.[q.question_id] === null || tool.gradesByStudent?.[student.student_id]?.[q.question_id] === undefined || tool.gradesByStudent?.[student.student_id]?.[q.question_id] === '') ? 'empty-cell' : ''}>
                                                {formatGrade(tool.gradesByStudent?.[student.student_id]?.[q.question_id])}
                                            </td>
                                        ))
                                    )}
                                    <td className="total-cell">{formatGrade(getStudentTotal(student.student_id))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-success" onClick={handleExportCLOMatrix}>
                        📥 {isAr ? 'تحميل Excel' : 'Export Excel'}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderStudentGradesModal = () => {
        const students = gradesData.students || [];
        const studentStats = students.map(student => {
            const tools = assessmentTools || [];
            const scores = [
                getScoreByCategory(student.student_id, 'hm'),
                getScoreByCategory(student.student_id, 'qz'),
                getScoreByCategory(student.student_id, 'md1'),
                getScoreByCategory(student.student_id, 'md2'),
                getScoreByCategory(student.student_id, 'lab')
            ];
            const semesterWorks = scores.reduce((a, b) => a + b, 0);
            const final = getScoreByCategory(student.student_id, 'final');
            const total = semesterWorks + final;
            const grade = calculateGrade(total);
            return { ...student, scores, semesterWorks, final, total, grade };
        });

        // Calculations for Course Report
        const counts = {
            'A+': 0, 'A': 0, 'B+': 0, 'B': 0, 'C+': 0, 'C': 0, 'D+': 0, 'D': 0, 'F': 0,
            'Pass': 0, 'Fail': 0, 'Total': students.length
        };

        studentStats.forEach(s => {
            if (counts[s.grade] !== undefined) counts[s.grade]++;
            if (s.grade === 'F') counts['Fail']++;
            else counts['Pass']++;
        });

        const getPercent = (val) => students.length > 0 ? ((val / students.length) * 100).toFixed(2) + '%' : '0%';
        const successPercent = students.length > 0 ? ((counts['Pass'] / students.length) * 100).toFixed(2) : '0.0';

        const simplifiedCounts = {
            'A': counts['A+'] + counts['A'],
            'B': counts['B+'] + counts['B'],
            'C': counts['C+'] + counts['C'],
            'D': counts['D+'] + counts['D'],
            'F': counts['F'],
            'Total': students.length
        };

        return (
            <div className="modal-overlay" onClick={() => setShowStudentGradesModal(false)}>
                <div className="modal-content clo-modal-wide" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Student Grades</h2>
                        <button className="btn btn-outline" onClick={() => setShowStudentGradesModal(false)}>
                            Close
                        </button>
                    </div>
                    <div className="modal-body print-area" style={{ background: 'white', padding: '20px', color: 'black' }}>
                        {renderCourseHeader()}
                        {/* Student Grades Table */}
                        <table className="report-table excel-style-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '120px' }}>Student ID</th>
                                    <th style={{ minWidth: '250px' }}>Student Name</th>
                                    <th style={{ width: '50px' }}>Hm</th>
                                    <th style={{ width: '50px' }}>Qz</th>
                                    <th style={{ width: '50px' }}>Md1</th>
                                    <th style={{ width: '50px' }}>Md2</th>
                                    <th style={{ width: '50px' }}>Lab</th>
                                    <th style={{ width: '80px', backgroundColor: '#e2e8f0', color: '#000' }}>Semester Works</th>
                                    <th style={{ width: '60px' }}>Final</th>
                                    <th style={{ width: '60px', fontWeight: 'bold' }}>Total</th>
                                    <th style={{ width: '60px' }}>Grade</th>
                                </tr>
                            </thead>
                            <tbody>
                                {studentStats.map((s) => (
                                    <tr key={s.student_id} className={s.grade === 'F' ? 'row-fail' : ''}>
                                        <td className="center-text">{s.student_code}</td>
                                        <td className="left-text px-15">
                                            {isAr ? s.full_name_ar : (s.full_name_en || s.full_name_ar)}
                                        </td>
                                        <td className={`center-text ${(s.scores[0] === null || s.scores[0] === undefined || s.scores[0] === '') ? 'empty-cell' : ''}`}>{formatGrade(s.scores[0])}</td>
                                        <td className={`center-text ${(s.scores[1] === null || s.scores[1] === undefined || s.scores[1] === '') ? 'empty-cell' : ''}`}>{formatGrade(s.scores[1])}</td>
                                        <td className={`center-text ${(s.scores[2] === null || s.scores[2] === undefined || s.scores[2] === '') ? 'empty-cell' : ''}`}>{formatGrade(s.scores[2])}</td>
                                        <td className={`center-text ${(s.scores[3] === null || s.scores[3] === undefined || s.scores[3] === '') ? 'empty-cell' : ''}`}>{formatGrade(s.scores[3])}</td>
                                        <td className={`center-text ${(s.scores[4] === null || s.scores[4] === undefined || s.scores[4] === '') ? 'empty-cell' : ''}`}>{formatGrade(s.scores[4])}</td>
                                        <td className="center-text" style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>{formatGrade(s.semesterWorks)}</td>
                                        <td className={`center-text ${(s.final === null || s.final === undefined || s.final === '') ? 'empty-cell' : ''}`}>{formatGrade(s.final)}</td>
                                        <td className="center-text total-cell">{formatGrade(s.total)}</td>
                                        <td className={`center-text grade-cell grade-${s.grade}`} style={{ fontWeight: 'bold' }}>{s.grade}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Course Report Section */}
                        <div style={{ marginTop: '30px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Succ %</div>
                                    <div style={{ padding: '10px', background: '#dcfce7', borderRadius: '4px', fontWeight: 'bold' }}>{successPercent}%</div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ textAlign: 'center', fontWeight: 'bold', background: '#f8fafc', padding: '5px', border: '1px solid #000' }}>Course Report</div>
                                    <table className="excel-style-table" style={{ width: '100%', marginTop: '-1px' }}>
                                        <thead>
                                            <tr>
                                                <th>A+</th><th>A</th><th>B+</th><th>B</th><th>C+</th><th>C</th><th>D+</th><th>D</th><th>F</th>
                                                <th style={{ fontSize: '0.8rem' }}>Denied Entry</th>
                                                <th style={{ fontSize: '0.8rem' }}>In Progress</th>
                                                <th style={{ fontSize: '0.8rem' }}>Incomplete</th>
                                                <th>Pass</th><th>Fail</th><th>Withdrawn</th><th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="center-text">{counts['A+']}</td>
                                                <td className="center-text">{counts['A']}</td>
                                                <td className="center-text">{counts['B+']}</td>
                                                <td className="center-text">{counts['B']}</td>
                                                <td className="center-text">{counts['C+']}</td>
                                                <td className="center-text">{counts['C']}</td>
                                                <td className="center-text">{counts['D+']}</td>
                                                <td className="center-text">{counts['D']}</td>
                                                <td className="center-text">{counts['F']}</td>
                                                <td className="center-text">0</td><td className="center-text">0</td><td className="center-text">0</td>
                                                <td className="center-text">{counts['Pass']}</td>
                                                <td className="center-text">{counts['Fail']}</td>
                                                <td className="center-text">0</td>
                                                <td className="center-text" style={{ fontWeight: 'bold' }}>{counts['Total']}</td>
                                            </tr>
                                            <tr>
                                                <td className="center-text text-purple">{getPercent(counts['A+'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['A'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['B+'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['B'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['C+'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['C'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['D+'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['D'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['F'])}</td>
                                                <td className="center-text text-purple">0%</td>
                                                <td className="center-text text-purple">0%</td>
                                                <td className="center-text text-purple">0%</td>
                                                <td className="center-text text-purple">{getPercent(counts['Pass'])}</td>
                                                <td className="center-text text-purple">{getPercent(counts['Fail'])}</td>
                                                <td className="center-text text-purple">0%</td>
                                                <td className="center-text text-purple">100%</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    {/* Simplified Summary */}
                                    <table className="excel-style-table" style={{ width: '300px', marginTop: '10px' }}>
                                        <thead>
                                            <tr><th>A</th><th>B</th><th>C</th><th>D</th><th>F</th><th>Total</th></tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="center-text">{simplifiedCounts['A']}</td>
                                                <td className="center-text">{simplifiedCounts['B']}</td>
                                                <td className="center-text">{simplifiedCounts['C']}</td>
                                                <td className="center-text">{simplifiedCounts['D']}</td>
                                                <td className="center-text">{simplifiedCounts['F']}</td>
                                                <td className="center-text" style={{ fontWeight: 'bold' }}>{simplifiedCounts['Total']}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-success" onClick={handleExportStudentGrades}>
                            📥 {isAr ? 'تحميل Excel' : 'Export Excel'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderMarksModal = () => {
        // Dynamic categories for UI
        const cats = [
            { nameEn: 'Midterm I Exam', nameAr: 'الامتحان النصفي الأول', cat: 'md1' },
            { nameEn: 'Midterm II Exam', nameAr: 'الامتحان النصفي الثاني', cat: 'md2' },
            { nameEn: 'Lab Work', nameAr: 'العملي', cat: 'lab' },
            { nameEn: 'Class Work (Quizzes, Assignments, etc.)', nameAr: 'أعمال الفصل (واجبات، كويزات، إلخ)', cat: 'class' },
            { nameEn: 'Final Exam', nameAr: 'الامتحان النهائي', cat: 'final' }
        ];

        return (
            <div className="modal-overlay" onClick={() => setShowMarksModal(false)}>
                <div className="modal-content clo-modal-wide" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>{isAr ? 'توزيع الدرجات' : 'Marks Distribution'}</h2>
                        <button className="btn btn-outline" onClick={() => setShowMarksModal(false)}>
                            {isAr ? 'إغلاق' : 'Close'}
                        </button>
                    </div>
                    <div className="modal-body print-area" style={{ background: 'white', padding: '20px', color: 'black' }}>
                        {renderCourseHeader()}
                        <table className="report-table excel-style-table">
                            <thead>
                                <tr>
                                    <th rowSpan="2" style={{ width: '50px' }}>No.</th>
                                    <th rowSpan="2" style={{ width: '120px' }}>{isAr ? 'رقم الطالب' : 'Student ID'}</th>
                                    <th rowSpan="2" style={{ minWidth: '250px' }}>{isAr ? 'اسم الطالب' : 'Student Name'}</th>
                                    <th>{isAr ? 'الامتحان النصفي الأول' : 'Midterm I Exam'}</th>
                                    <th>{isAr ? 'الامتحان النصفي الثاني' : 'Midterm II Exam'}</th>
                                    <th>{isAr ? 'العملي' : 'Lab Work'}</th>
                                    <th style={{ fontSize: '0.85rem' }}>{isAr ? 'أعمال الفصل (واجبات، كويزات، إلخ)' : 'Class Work (Quizzes, Assignments, etc.)'}</th>
                                    <th>{isAr ? 'الامتحان النهائي' : 'Final Exam'}</th>
                                    <th style={{ width: '80px' }}>{isAr ? 'المجموع' : 'Total'}</th>
                                </tr>
                                <tr>
                                    <th>{getMaxByCategory('md1')}</th>
                                    <th>{getMaxByCategory('md2')}</th>
                                    <th>{getMaxByCategory('lab')}</th>
                                    <th>{+(getMaxByCategory('hm') + getMaxByCategory('qz')).toFixed(2)}</th>
                                    <th>{getMaxByCategory('final')}</th>
                                    <th style={{ fontWeight: 'bold' }}>
                                        {+(getMaxByCategory('md1') + getMaxByCategory('md2') + getMaxByCategory('lab') + getMaxByCategory('hm') + getMaxByCategory('qz') + getMaxByCategory('final')).toFixed(2)}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {gradesData.students.map((student, idx) => (
                                    <tr key={student.student_id}>
                                        <td className="center-text">{idx + 1}</td>
                                        <td className="center-text">{student.student_code}</td>
                                        <td className="left-text px-15">
                                            {isAr ? student.full_name_ar : (student.full_name_en || student.full_name_ar)}
                                        </td>
                                        <td className="center-text">{getScoreByCategory(student.student_id, 'md1')}</td>
                                        <td className="center-text">{getScoreByCategory(student.student_id, 'md2')}</td>
                                        <td className="center-text">{getScoreByCategory(student.student_id, 'lab')}</td>
                                        <td className="center-text">{+(getScoreByCategory(student.student_id, 'hm') + getScoreByCategory(student.student_id, 'qz')).toFixed(2)}</td>
                                        <td className="center-text">{getScoreByCategory(student.student_id, 'final')}</td>
                                        <td className="total-cell center-text">{getStudentTotal(student.student_id).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-success" onClick={handleExportMarksDistribution}>
                            📥 {isAr ? 'تحميل Excel' : 'Export Excel'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderCLOFormModal = () => {
        const domains = {
            'Knowledge': ['K1', 'K2', 'K3', 'K4', 'K5'],
            'Skills': ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8'],
            'Values': ['V1', 'V2', 'V3', 'V4', 'V5']
        };

        const calculateCellData = (type, tool) => {
            const matchingQuestions = tool.questions.filter(q => q.question_type?.toUpperCase() === type);
            if (matchingQuestions.length === 0) return null;

            let max = matchingQuestions.reduce((sum, q) => sum + parseFloat(q.max_score), 0);
            let scoreSum = 0;
            let count = 0;

            gradesData.students.forEach(student => {
                matchingQuestions.forEach(q => {
                    const score = tool.gradesByStudent?.[student.student_id]?.[q.question_id];
                    if (score !== undefined && score !== null && score !== '') {
                        scoreSum += parseFloat(score);
                        count++;
                    }
                });
            });

            const avg = count > 0 ? scoreSum / gradesData.students.length : 0;
            return { avg: avg.toFixed(2), max: max.toFixed(2) }; // Using integers as per screenshot (13/15)
        };

        const calculateRowTotal = (type) => {
            let totalMax = 0;
            let totalAvg = 0;
            const toolData = {};

            assessmentTools.forEach(tool => {
                const data = calculateCellData(type, tool);
                if (data) {
                    toolData[tool.tool_id] = data;
                    totalMax += parseFloat(data.max);
                    totalAvg += parseFloat(data.avg);
                }
            });

            const percentage = totalMax > 0 ? ((totalAvg / totalMax) * 100).toFixed(2) : '0.00';
            return { toolData, totalMax, totalAvg: totalAvg.toFixed(2), percentage };
        };

        return (
            <div className="modal-overlay clo-print-modal" onClick={() => setShowCLOFormModal(false)}>
                <div className="modal-content clo-modal-wide" style={{ maxWidth: '85%', width: 'auto' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>{isAr ? 'نموذج قياس مخرجات التعلم' : 'Program Learning Outcomes (PLOs) Measurement'}</h2>
                        <button className="btn btn-outline" onClick={() => setShowCLOFormModal(false)}>
                            {isAr ? 'إغلاق' : 'Close'}
                        </button>
                    </div>
                    <div className="modal-body" style={{ background: 'white', padding: '20px', color: 'black' }}>
                        {renderCourseHeader()}

                        <table className="clo-form-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
                            <thead>
                                {/* Row 1: Main Headers */}
                                <tr className="bg-blue-light">
                                    <th colSpan="2" style={{ width: '150px' }}>Learning Outcomes</th>
                                    <th colSpan={assessmentTools.length + 1}>Assessment methods</th>
                                    <th colSpan="3">PLOs</th>
                                </tr>
                                {/* Row 2: Sub Headers */}
                                <tr className="bg-blue-light">
                                    <th className="bg-yellow-light">Domain</th>
                                    <th className="bg-yellow-light">Code</th>
                                    {assessmentTools.map(tool => (
                                        <th key={tool.tool_id} className="bg-yellow-light" style={{ minWidth: '80px', fontSize: '0.9rem' }}>
                                            {tool.tool_name_en || tool.tool_name_ar}
                                        </th>
                                    ))}
                                    <th className="bg-yellow-light" style={{ minWidth: '80px' }}>Total (100)</th>
                                    <th className="bg-yellow-light">Actual level</th>
                                    <th className="bg-yellow-light">Target level</th>
                                </tr>
                                {/* Row 3: Max Marks */}
                                <tr className="bg-blue-light">
                                    <th className="bg-blue-pale"></th>
                                    <th className="bg-blue-pale"></th>
                                    {assessmentTools.map(tool => {
                                        const toolMax = tool.questions.reduce((sum, q) => sum + parseFloat(q.max_score), 0);
                                        return (
                                            <th key={tool.tool_id} style={{ fontSize: '0.85rem' }}>
                                                ( {toolMax} ) Marks
                                            </th>
                                        );
                                    })}
                                    <th></th>
                                    <th style={{ background: 'white' }}>100%</th>
                                    <th style={{ background: 'white' }}>100%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(domains).map(([domain, types], dIdx) => {
                                    return types.map((type, tIdx) => {
                                        const { toolData, totalMax, totalAvg, percentage } = calculateRowTotal(type);
                                        // Only show rows if requested? Or showing frame? Screenshot shows all K1-K5
                                        // Let's assume always showing check boxes.
                                        const isRelevant = totalMax > 0;

                                        return (
                                            <tr key={type} style={{ height: '35px' }}>
                                                {tIdx === 0 && (
                                                    <td rowSpan={types.length} className="domain-rspan">
                                                        {domain}
                                                    </td>
                                                )}
                                                <td style={{ fontWeight: 'bold', background: 'white' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 5px' }}>
                                                        {type}
                                                        <input type="checkbox" checked={isRelevant} readOnly style={{ marginLeft: '5px' }} />
                                                    </div>
                                                </td>
                                                {assessmentTools.map(tool => (
                                                    <td key={tool.tool_id} style={{ background: 'white' }}>
                                                        {toolData[tool.tool_id]
                                                            ? `${toolData[tool.tool_id].avg}/${toolData[tool.tool_id].max}`
                                                            : ''
                                                        }
                                                    </td>
                                                ))}
                                                <td style={{ background: 'white', fontWeight: 'bold' }}>
                                                    {isRelevant ? `${totalAvg}/${totalMax}` : ''}
                                                </td>
                                                <td style={{ background: 'white' }}>
                                                    {isRelevant ? `${percentage}%` : ''}
                                                </td>
                                                <td style={{ background: 'white' }}>
                                                    {isRelevant ? '70%' : ''}
                                                </td>
                                            </tr>
                                        );
                                    });
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-print" onClick={() => window.print()}>
                            <span className="material-icons">print</span>
                            {isAr ? 'طباعة' : 'Print'}
                        </button>
                    </div>
                </div >
            </div >
        );
    };

    const handleKeyDown = (e) => {
        const { key } = e;
        const row = parseInt(e.target.getAttribute('data-row'));
        const col = parseInt(e.target.getAttribute('data-col'));

        let nextRow = row;
        let nextCol = col;

        if (key === 'ArrowDown') {
            e.preventDefault();
            nextRow = row + 1;
        } else if (key === 'ArrowUp') {
            e.preventDefault();
            nextRow = row - 1;
        } else if (key === 'ArrowRight') {
            nextCol = isAr ? col - 1 : col + 1;
        } else if (key === 'ArrowLeft') {
            nextCol = isAr ? col + 1 : col - 1;
        } else if (key === 'Enter') {
            e.preventDefault();
            nextRow = row + 1;
        } else {
            return;
        }

        const nextInput = document.querySelector(`input[data-row="${nextRow}"][data-col="${nextCol}"]`);
        if (nextInput) {
            nextInput.focus();
            nextInput.select();
        }
    };

    if (loading) return <div className="loading-spinner">{t('common.loading')}</div>;

    return (
        <div className="clos-page fade-in" dir={isAr ? 'rtl' : 'ltr'}>
            <div className="clos-header">
                <div className="course-info">
                    <button className="btn-back" onClick={() => navigate('/teacher/courses')}>
                        <span className="material-icons">arrow_back</span>
                    </button>
                    <div>
                        <h1>{isAr ? course?.course_name_ar : course?.course_name_en}</h1>
                        <span className="course-code">{course?.course_code}</span>
                    </div>
                </div>

                <div className="matrix-controls">
                    {saving && <span className="saving-indicator">{isAr ? 'جاري الحفظ...' : 'Saving...'}</span>}
                    <button
                        className={`btn-batch-save ${(Object.keys(unsavedGrades).length > 0 || Object.keys(unsavedMaxScores).length > 0) ? 'has-unsaved pulse' : ''}`}
                        onClick={handleBulkSave}
                        disabled={saving || (Object.keys(unsavedGrades).length === 0 && Object.keys(unsavedMaxScores).length === 0)}
                    >
                        <span className="material-icons">save</span>
                        {isAr ? 'حفظ التعديلات' : 'Save Changes'}
                        {(Object.keys(unsavedGrades).length + Object.keys(unsavedMaxScores).length > 0) && (
                            <span className="unsaved-count">{Object.keys(unsavedGrades).length + Object.keys(unsavedMaxScores).length}</span>
                        )}
                    </button>
                </div>
            </div>

            {/* Stats Cards - Above Table */}
            <div className="matrix-footer">
                <div className="stat-card">
                    <div className="stat-icon primary">
                        <span className="material-icons">people</span>
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{gradesData.students.length}</span>
                        <span className="stat-label">{isAr ? 'الطلاب' : 'Students'}</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon success">
                        <span className="material-icons">check_circle</span>
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">
                            {(() => {
                                const passed = gradesData.students.filter(s => getStudentTotal(s.student_id) >= 60).length;
                                return gradesData.students.length > 0 ? Math.round((passed / gradesData.students.length) * 100) : 0;
                            })()}%
                        </span>
                        <span className="stat-label">{isAr ? 'النجاح' : 'Pass'}</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon warning">
                        <span className="material-icons">functions</span>
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">
                            {(() => {
                                if (gradesData.students.length === 0) return 0;
                                const sum = gradesData.students.reduce((acc, s) => acc + getStudentTotal(s.student_id), 0);
                                return (sum / gradesData.students.length).toFixed(2);
                            })()}
                        </span>
                        <span className="stat-label">{isAr ? 'المتوسط' : 'Avg'}</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon danger">
                        <span className="material-icons">error</span>
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">
                            {gradesData.students.filter(s => getStudentTotal(s.student_id) < 60).length}
                        </span>
                        <span className="stat-label">{isAr ? 'متعثرين' : 'At Risk'}</span>
                    </div>
                </div>
            </div>

            {/* Report Buttons */}
            <div className="report-buttons-container">
                <button className="report-btn" style={{ background: '#1e293b', color: 'white' }} onClick={handleExportAll}>
                    <span className="material-icons">download</span>
                    <span>{isAr ? 'تحميل' : 'Export'}</span>
                </button>
                <button className="report-btn report-btn-matrix" onClick={() => setShowCLOMatrixModal(true)}>
                    <span className="material-icons">grid_on</span>
                    <span>{isAr ? 'المصفوفة' : 'Matrix'}</span>
                </button>
                <button className="report-btn report-btn-form" onClick={() => setShowCLOFormModal(true)}>
                    <span className="material-icons">description</span>
                    <span>{isAr ? 'النموذج' : 'Form'}</span>
                </button>
                <button className="report-btn report-btn-marks" onClick={() => setShowMarksModal(true)}>
                    <span className="material-icons">assessment</span>
                    <span>{isAr ? 'التوزيع' : 'Marks'}</span>
                </button>
                <button className="report-btn report-btn-grades" onClick={() => setShowStudentGradesModal(true)}>
                    <span className="material-icons">school</span>
                    <span>{isAr ? 'الدرجات' : 'Grades'}</span>
                </button>
            </div>

            {/* Color Legend */}
            <div className="color-legend">
                <div className="color-legend-item">
                    <span className="color-box invalid"></span>
                    <span>{isAr ? 'درجة غير صحيحة (أكبر من العظمى)' : 'Invalid Grade (> Max)'}</span>
                </div>
                <div className="color-legend-item">
                    <span className="color-box unsaved"></span>
                    <span>{isAr ? 'تعديلات غير محفوظة' : 'Unsaved Changes'}</span>
                </div>
                <div className="color-legend-item">
                    <span className="color-box empty"></span>
                    <span>{isAr ? 'لم يتم رصد الدرجة' : 'Grade Not Entered'}</span>
                </div>
            </div>

            <div className="matrix-container">
                <table className="matrix-table">
                    <thead>
                        {/* Row 0: Delete & Add Actions */}
                        <tr>
                            <th colSpan="3" className="sticky-col no-bg"></th>
                            {assessmentTools.map(tool => (
                                <th
                                    key={`del-${tool.tool_id}`}
                                    colSpan={tool.questions.length || 1}
                                    className="tool-delete-cell"
                                >
                                    {assessmentTools.length > 1 && (
                                        <button
                                            className="btn-delete-tool"
                                            onClick={() => handleDeleteTool(tool.tool_id, tool.tool_name_en || tool.tool_name_ar)}
                                            title={isAr ? 'حذف' : 'Delete'}
                                        >
                                            <span className="material-icons">delete_outline</span>
                                        </button>
                                    )}
                                </th>
                            ))}
                            <th className="no-bg add-tool-header-cell">
                                {missingTools.length > 0 && (
                                    <button
                                        className="btn-add-tool-header"
                                        onClick={() => setShowAddToolModal(true)}
                                        title={isAr ? 'إضافة أداة تقييم' : 'Add Assessment Tool'}
                                    >
                                        <span className="material-icons">add</span>
                                    </button>
                                )}
                            </th>
                        </tr>
                        {/* Row 1: Assessment Tool Names */}
                        <tr>
                            <th rowSpan="4" className="sticky-col sticky-col-idx">#</th>
                            <th rowSpan="4" className="sticky-col sticky-col-id">{isAr ? 'رقم الطالب' : 'ID'}</th>
                            <th className="sticky-col sticky-col-name header-label-cell">
                                {isAr ? 'أداة التقييم' : 'Assessment Tool'}
                            </th>
                            {assessmentTools.map(tool => (
                                <th
                                    key={tool.tool_id}
                                    colSpan={tool.questions.length || 1}
                                    className={`tool-header-cell ${tool.colorClass}`}
                                >
                                    <div className="tool-header-with-actions">
                                        {tool.tool_name_en || tool.tool_name_ar}
                                        <button
                                            className="btn-add-question"
                                            onClick={() => handleAddQuestion(tool.tool_id)}
                                            title={isAr ? 'إضافة سؤال' : 'Add Question'}
                                        >
                                            <span className="material-icons">add_circle_outline</span>
                                        </button>
                                    </div>
                                </th>
                            ))}
                            <th className="total-header total-label sticky-col sticky-col-last">{isAr ? 'المجموع' : 'Total'}</th>
                        </tr>
                        {/* Row 2: Max Scores */}
                        <tr className="header-row-max">
                            <th className="sticky-col sticky-col-name header-label-cell">
                                {isAr ? 'الدرجة القصوى' : 'Max Score'}
                            </th>
                            {(() => {
                                let colIdx = 0;
                                return assessmentTools.flatMap(tool =>
                                    tool.questions.map((q, idx) => {
                                        const currentCol = colIdx++;
                                        return (
                                            <th key={`max-${tool.tool_id}-${idx}`} className="max-score-header no-padding">
                                                <input
                                                    type="number"
                                                    className={`header-input ${unsavedMaxScores[q.question_id] !== undefined ? 'unsaved-input' : ''}`}
                                                    defaultValue={formatGrade(q.max_score)}
                                                    key={q.max_score}
                                                    data-row="-1"
                                                    data-col={currentCol}
                                                    onKeyDown={handleKeyDown}
                                                    onBlur={(e) => {
                                                        let v = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                        if (v < 0) v = 0;
                                                        v = parseFloat(v).toFixed(2);
                                                        e.target.value = v;
                                                        if (parseFloat(v) !== q.max_score) {
                                                            handleMaxScoreInput(q.question_id, v);
                                                        }
                                                    }}
                                                    min="0"
                                                    step="0.5"
                                                />
                                            </th>
                                        );
                                    })
                                );
                            })()}
                            <th className="total-max-header sticky-col sticky-col-last">
                                {formatGrade(assessmentTools.reduce((acc, tool) => acc + tool.questions.reduce((s, q) => s + parseFloat(q.max_score || 0), 0), 0))}
                            </th>
                        </tr>
                        {/* Row 3: Averages */}
                        <tr className="average-row">
                            <th className="sticky-col sticky-col-name header-label-cell" style={{ textAlign: 'center' }}>
                                {isAr ? 'المتوسط' : 'Average'}
                            </th>
                            {assessmentTools.flatMap(tool =>
                                tool.questions.map((q, qIdx) => (
                                    <th key={`avg-${q.question_id}`} className="average-header-cell">
                                        {getQuestionAverage(q.question_id).toFixed(2)}
                                    </th>
                                ))
                            )}
                            <th className="total-avg-cell">
                                {formatGrade(gradesData.students.length > 0
                                    ? gradesData.students.reduce((sum, s) => sum + getStudentTotal(s.student_id), 0) / gradesData.students.length
                                    : 0
                                )}
                            </th>
                        </tr>
                        {/* Row 4: PLOs */}
                        <tr className="header-row-clos">
                            <th className="sticky-col sticky-col-name header-label-cell">
                                {isAr ? 'مخرجات التعلم' : 'PLOs'}
                            </th>
                            {assessmentTools.flatMap(tool =>
                                tool.questions.map((q, idx) => (
                                    <th key={`clo-${tool.tool_id}-${idx}`} className="clo-label-cell no-padding relative-cell">
                                        {tool.questions.length > 1 && (
                                            <button
                                                className="btn-delete-question"
                                                onClick={() => handleDeleteQuestion(q.question_id)}
                                                title={isAr ? 'حذف السؤال' : 'Delete Question'}
                                            >
                                                ×
                                            </button>
                                        )}
                                        <select
                                            className="header-select"
                                            value={q.question_type}
                                            onChange={(e) => handleQuestionUpdate(q.question_id, { questionType: e.target.value })}
                                        >
                                            {CLO_TYPES.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </th>
                                ))
                            )}
                            <th className="header-label-cell sticky-col sticky-col-last"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {gradesData.students.map((student, idx) => (
                            <tr key={student.student_id}>
                                <td className="sticky-col sticky-col-idx">{idx + 1}</td>
                                <td className="sticky-col sticky-col-id">{student.student_code}</td>
                                <td className="sticky-col sticky-col-name">
                                    {isAr ? student.full_name_ar : (student.full_name_en || student.full_name_ar)}
                                </td>
                                {(() => {
                                    let colIdx = 0;
                                    return assessmentTools.flatMap(tool =>
                                        tool.questions.map((q, qIdx) => {
                                            const currentCol = colIdx++;
                                            const grade = tool.gradesByStudent?.[student.student_id]?.[q.question_id];
                                            return (
                                                <td
                                                    key={`${student.student_id}-${tool.tool_id}-${qIdx}`}
                                                    className={(grade === null || grade === undefined || grade === '') ? 'empty-cell' : ''}
                                                >
                                                    <input
                                                        type="number"
                                                        className={`grade-input 
                                                            ${(parseFloat(grade) > parseFloat(q.max_score)) ? 'invalid-input' : ''} 
                                                        ${(unsavedGrades[`${student.student_id}_${q.question_id}`] !== undefined && parseFloat(grade) <= parseFloat(q.max_score)) ? 'unsaved-input' : ''} 
                                                        ${(grade === null || grade === undefined || grade === '') ? 'empty-input' : ''}`}
                                                        defaultValue={(grade !== null && grade !== undefined && grade !== '') ? formatGrade(grade) : ''}
                                                        data-row={idx}
                                                        data-col={currentCol}
                                                        onKeyDown={handleKeyDown}
                                                        onBlur={(e) => {
                                                            let v = e.target.value === '' ? null : parseFloat(e.target.value);
                                                            if (v !== null) {
                                                                if (v > q.max_score) v = q.max_score;
                                                                if (v < 0) v = 0;
                                                                v = parseFloat(v).toFixed(2);
                                                            }
                                                            e.target.value = v === null ? '' : v;
                                                            handleGradeInput(student.student_id, q.question_id, v, q.max_score);
                                                        }}
                                                        onInput={(e) => {
                                                            const val = parseFloat(e.target.value);
                                                            if (val > q.max_score) e.target.value = q.max_score;
                                                            if (val < 0) e.target.value = 0;
                                                        }}
                                                        min="0"
                                                        max={q.max_score}
                                                        step="0.5"
                                                    />
                                                </td>
                                            );
                                        })
                                    );
                                })()}
                                <td className="total-cell">
                                    {getStudentTotal(student.student_id).toFixed(2)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modals */}
            {showCLOMatrixModal && renderCLOMatrixModal()}
            {showCLOFormModal && renderCLOFormModal()}
            {showMarksModal && renderMarksModal()}
            {showStudentGradesModal && renderStudentGradesModal()}

            {
                showAddToolModal && (
                    <div className="modal-overlay" onClick={() => setShowAddToolModal(false)}>
                        <div className="modal-content small-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{isAr ? 'إضافة أداة تقييم' : 'Add Assessment Tool'}</h2>
                                <button className="btn-close" onClick={() => setShowAddToolModal(false)}>×</button>
                            </div>
                            <div className="modal-body">
                                <div className="missing-tools-list">
                                    {missingTools.map(tool => (
                                        <button
                                            key={tool.nameEn}
                                            className="missing-tool-item"
                                            onClick={() => handleAddTool(tool)}
                                        >
                                            <span className="material-icons">add_circle</span>
                                            {isAr ? tool.nameAr : tool.nameEn}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default CLOs;
